import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, formatApiError } from "../components/AuthContext";
import { Sparkles, Check, X, RefreshCw, Wand2, Loader2 } from "lucide-react";
import { logError } from "../lib/log";
import { checkIdGaps } from "../components/IdGapHost";

const KIND_LABEL = {
  anomaly: "Anomaly",
  triage: "Job triage",
  client_health: "Client health",
  smart_job: "Drafted job",
  email_triage: "From email",
};

const KIND_ACCENT = {
  anomaly: "var(--accent)",
  triage: "var(--moss)",
  client_health: "var(--ink)",
  smart_job: "var(--accent)",
  email_triage: "var(--accent)",
};

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function Insights() {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [scanning, setScanning] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [error, setError] = useState("");
  const [pendingId, setPendingId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const r = await axios.get(`${API_BASE}/insights/proposals${q}`);
      setProposals(r.data);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { reload(); }, [reload]);

  const runScan = async () => {
    setScanning(true); setError("");
    try {
      const r = await axios.post(`${API_BASE}/insights/scan`);
      const created = r.data?.proposals || [];
      if (filter === "pending" || filter === "all") {
        setProposals((rows) => [...created, ...rows]);
      }
      toast.success(created.length ? `Found ${created.length} anomalies` : "No new anomalies detected");
      pingPending();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg); toast.error(msg);
    }
    finally { setScanning(false); }
  };

  const draftJob = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    setDrafting(true); setError("");
    try {
      const r = await axios.post(`${API_BASE}/insights/smart-job`, { prompt: prompt.trim() });
      if (filter === "pending" || filter === "all") {
        setProposals((rows) => [r.data, ...rows]);
      }
      setPrompt("");
      toast.success("Drafted a job — review and accept below");
      pingPending();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg); toast.error(msg);
    }
    finally { setDrafting(false); }
  };

  const accept = async (p) => {
    setPendingId(p.proposal_id); setError("");
    try {
      const r = await axios.post(`${API_BASE}/insights/proposals/${p.proposal_id}/accept`);
      setProposals((rows) => rows.map((x) => (x.proposal_id === p.proposal_id ? r.data : x))
                                 .filter((x) => filter === "all" || x.status === filter));
      const eff = r.data?.side_effect;
      toast.success(eff?.performed ? `Created job ${eff.job_id}` : "Proposal accepted");
      if (eff?.type === "create_job" && eff.job_id) checkIdGaps({ jobIds: [eff.job_id], source: "Suggested job created" });
      pingPending();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg); toast.error(msg);
    }
    finally { setPendingId(null); }
  };

  const dismiss = async (p) => {
    setPendingId(p.proposal_id); setError("");
    try {
      const r = await axios.post(`${API_BASE}/insights/proposals/${p.proposal_id}/dismiss`);
      setProposals((rows) => rows.map((x) => (x.proposal_id === p.proposal_id ? r.data : x))
                                 .filter((x) => filter === "all" || x.status === filter));
      toast("Proposal dismissed");
      pingPending();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg); toast.error(msg);
    }
    finally { setPendingId(null); }
  };

  // ping the global pending count refresher (set up by PortalLayout)
  const pingPending = () => {
    try { window.dispatchEvent(new CustomEvent("insights:refresh-pending")); }
    catch (err) { logError("insights:dispatch-event", err); }
  };

  return (
    <div className="px-10 py-12 max-w-[1240px]" data-testid="insights-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="overline mb-2">AI Bookkeeper · propose-not-execute</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>
            Insights<em className="italic" style={{ color: "var(--accent)" }}> & proposals</em>
          </h1>
          <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
            Claude scans your firm's data and surfaces anomalies, drafts jobs, and writes client health
            summaries. Nothing is applied automatically — review and accept what's useful.
          </p>
        </div>
        <button onClick={runScan} disabled={scanning} className="btn-primary" data-testid="insights-scan-btn">
          {scanning ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {scanning ? "Scanning…" : "Run anomaly scan"}
        </button>
      </div>

      {/* Smart job composer */}
      <form onSubmit={draftJob} className="card p-5 mb-8" data-testid="insights-smart-job-form">
        <div className="flex items-center gap-2 mb-2">
          <Wand2 size={14} style={{ color: "var(--accent)" }} />
          <div className="overline">Draft a job from a sentence</div>
        </div>
        <div className="flex gap-3">
          <input
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g. Year-end 1099 prep for Greta Lindqvist due Jan 31"
            data-testid="insights-smart-job-prompt"
            className="flex-1 bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]"
          />
          <button type="submit" disabled={drafting || !prompt.trim()} className="btn-primary" data-testid="insights-smart-job-submit">
            {drafting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {drafting ? "Drafting…" : "Draft"}
          </button>
        </div>
        <div className="text-[11px] text-[var(--ink-3)] mt-2">
          Claude proposes title, stages, budget, and matches a client. Review before accepting.
        </div>
      </form>

      {/* Filter pills */}
      <div className="flex gap-2 mb-5" data-testid="insights-filter">
        {[
          ["pending", "Pending"],
          ["accepted", "Accepted"],
          ["dismissed", "Dismissed"],
          ["all", "All"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`insights-filter-${k}`}
            className={`px-3 py-1.5 text-[12px] rounded-md border transition ${
              filter === k
                ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]"
                : "bg-[var(--paper)] text-[var(--ink-2)] border-[var(--border)] hover:border-[var(--ink-3)]"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {error && <div className="text-[13px] text-[var(--accent)] mb-4" data-testid="insights-error">{error}</div>}

      {loading ? (
        <div className="text-[var(--ink-3)]" data-testid="insights-loading">Loading…</div>
      ) : proposals.length === 0 ? (
        <div className="card p-10 text-center" data-testid="insights-empty">
          <Sparkles size={28} style={{ color: "var(--accent)" }} className="mx-auto mb-3" />
          <div className="font-heading text-xl mb-1" style={{ fontWeight: 500 }}>No proposals yet</div>
          <div className="text-[var(--ink-3)] text-sm">Run a scan or draft a job above to get started.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="insights-list">
          {proposals.map((p) => (
            <ProposalCard
              key={p.proposal_id}
              p={p}
              busy={pendingId === p.proposal_id}
              onAccept={() => accept(p)}
              onDismiss={() => dismiss(p)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ProposalCard({ p, busy, onAccept, onDismiss }) {
  const isPending = p.status === "pending";
  const hasAction = !!p.action?.type;
  return (
    <div className="card p-5" data-testid={`proposal-${p.proposal_id}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="badge" style={{ background: KIND_ACCENT[p.kind], color: "var(--paper)" }}>
            {KIND_LABEL[p.kind] || p.kind}
          </span>
          <span className="overline text-[10px]" style={{ color: "var(--ink-3)" }}>
            {fmtTime(p.created_at)}
          </span>
          {p.status !== "pending" && (
            <span className="badge" style={{ background: "var(--subtle)", color: "var(--ink-2)" }}>
              {p.status}
            </span>
          )}
        </div>
        {isPending && (
          <div className="flex gap-2">
            {hasAction && (
              <button onClick={onAccept} disabled={busy} className="btn-primary" data-testid={`proposal-accept-${p.proposal_id}`}>
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Accept
              </button>
            )}
            <button onClick={onDismiss} disabled={busy} className="btn-secondary" data-testid={`proposal-dismiss-${p.proposal_id}`}>
              <X size={12} /> Dismiss
            </button>
          </div>
        )}
      </div>
      <div className="font-heading text-lg mb-2" style={{ fontWeight: 500 }}>{p.title}</div>
      <div className="text-[14px] text-[var(--ink-2)] whitespace-pre-wrap leading-relaxed" data-testid={`proposal-body-${p.proposal_id}`}>
        {p.body}
      </div>
      {p.side_effect?.performed && (
        <div className="mt-3 text-[12px] font-mono text-[var(--moss)]" data-testid={`proposal-effect-${p.proposal_id}`}>
          ✓ Created {p.side_effect.type === "create_job" ? `job ${p.side_effect.job_id}` : p.side_effect.type}
        </div>
      )}
    </div>
  );
}
